import { type FC, useCallback, useMemo } from 'react';
import { HttpError, useDataProvider, useTranslate } from 'ra-core';
import { z } from 'zod';
import { type AdminDataProvider } from '../../../api/adminDataProvider';
import {
  type GridRowValidator,
  type RowProcessor,
} from '../../BulkOperation/types';
import { BulkOperation } from '../../BulkOperation/BulkOperation';
import { type KpiResponseDto } from '../types';
import { zIsNumber } from './zIsNumber';
import { useQueryClient } from '@tanstack/react-query';

export const BulkCreateKpiUnitDataButton: FC<{ kpi: KpiResponseDto }> = ({
  kpi,
}) => {
  const queryClient = useQueryClient();
  const translate = useTranslate();
  const importDataSchema = useMemo(
    () =>
      z.object({
        Target: zIsNumber(),
        Actual: zIsNumber(),
        Score: zIsNumber(),
        'Unit ID': z.string().optional(),
        'Unit name': z.string(),
      }),
    []
  );

  const csvSchema = useMemo(
    () => z.array(importDataSchema),
    [importDataSchema]
  );

  const dataProvider = useDataProvider<AdminDataProvider>();

  const validateRow = useCallback<
    GridRowValidator<z.infer<typeof importDataSchema>>
  >(
    async ({ data: csvRecord }) => {
      const res = await dataProvider.getList('kpi-data', {
        filter: {
          kpiId: kpi.id,
          exactAssigneeUnitName: csvRecord['Unit name'],
        },
        pagination: { page: 1, perPage: 1 },
        sort: { field: 'id', order: 'ASC' },
      });

      const [unitInApi] = res.data ?? [];

      if (!unitInApi) {
        return (oldData) => ({
          ...oldData,
          status: 'error',
          data: csvRecord,
          asyncErrors: [
            {
              message: translate('moonstar.kpiData.import.unitNotFound'),
              property: 'Unit name',
            },
          ],
        });
      }

      return (oldData) => ({
        ...oldData,
        status: 'success',
        asyncErrors: [],
        data: {
          ...csvRecord,
          kpiId: kpi.id,
          'Unit ID': csvRecord['Unit ID'] || `${unitInApi.id}`,
          Actual: csvRecord['Actual'],
          Target: csvRecord['Target'],
          Score: csvRecord['Score'],
        },
      });
    },
    [dataProvider, kpi, translate]
  );

  const processRow = useCallback<
    RowProcessor<z.infer<typeof importDataSchema>>
  >(
    async (row) => {
      const response = await dataProvider
        .create('kpi-data', {
          data: {
            assigneeUnitId: row.data['Unit ID']
              ? parseInt(row.data['Unit ID'], 10)
              : undefined,
            kpiId: kpi.id,
            actual: row.data['Actual'],
            target: row.data['Target'],
            score: row.data['Score'],
          },
        })
        .catch((err: unknown) => {
          if (err instanceof HttpError) {
            // NOTE: HttpError won't return the response body, as it isn't JSON, so it ignores it
            // https://github.com/marmelab/react-admin/blob/0a7a6e7f612990dca093fbbf4cfa531ead4583a2/packages/ra-core/src/dataProvider/fetch.ts#L67
            return err;
          }

          throw err;
        });

      if (response instanceof HttpError) {
        return (oldData) => ({
          ...oldData,
          status: 'error',
          data: row.data,
          errors: [
            response.body?.message ||
              translate('moonstar.kpiData.import.unexpectedError'),
          ],
        });
      } else {
        void queryClient.invalidateQueries({ queryKey: ['kpi-data'] });
        return (oldData) => ({
          ...oldData,
          status: 'success',
          data: row.data,
        });
      }
    },
    [dataProvider, kpi, queryClient, translate]
  );

  return (
    <BulkOperation
      label="moonstar.import.label"
      resource="kpi-data"
      schema={csvSchema}
      exampleValues={[
        {
          'Unit name': 'Team Awesome',
          Actual: '900',
          Target: '1000',
          Score: '20',
        },
      ]}
      validateRow={validateRow}
      processRow={processRow}
      validationRateLimitDelay={500}
      processingRateLimitDelay={500}
    />
  );
};
